import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './MultiSelect.css';

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Appended to `multi-select-container` for scoped styling (e.g. RAG document picker). */
  className?: string;
  /** Extra class on the options dropdown panel. */
  dropdownClassName?: string;
  /** Render dropdown in a body portal (avoids clipping inside scrollable modals). */
  portal?: boolean;
  /** Max dropdown height in px when portaled or in constrained layouts. */
  maxDropdownHeight?: number;
  /**
   * `inline` — option list is always visible under the chip row (no click-to-open).
   * Default `dropdown` — collapsible list with chevron.
   */
  listLayout?: 'dropdown' | 'inline';
  /** Hide top input/chips row (useful for always-visible inline list). */
  showInput?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className,
  dropdownClassName,
  portal = false,
  maxDropdownHeight = 300,
  listLayout = 'dropdown',
  showInput = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownLayout, setDropdownLayout] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInline = listLayout === 'inline';
  const showDropdown = isInline || isOpen;

  useLayoutEffect(() => {
    if (!portal || !showDropdown || isInline) {
      setDropdownLayout(null);
      return;
    }

    const updateLayout = () => {
      const trigger = inputRef.current ?? containerRef.current;
      const dropdown = dropdownRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewH = window.innerHeight;
      const viewW = window.innerWidth;
      const gap = 4;
      const width = Math.min(rect.width, viewW - 24);
      const spaceBelow = viewH - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openDown = spaceBelow >= spaceAbove;
      const available = (openDown ? spaceBelow : spaceAbove) - 12;
      const maxHeight = Math.max(180, Math.min(maxDropdownHeight, available));
      const contentHeight = dropdown?.scrollHeight ?? maxHeight;
      const placedHeight = Math.min(contentHeight, maxHeight);
      const top = openDown
        ? rect.bottom + gap
        : Math.max(gap, rect.top - placedHeight);

      let left = rect.left;
      if (left + width > viewW - 12) left = viewW - width - 12;
      if (left < 12) left = 12;

      setDropdownLayout({ top, left, width, maxHeight });
    };

    updateLayout();
    const raf = requestAnimationFrame(updateLayout);
    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
    };
  }, [portal, showDropdown, isInline, maxDropdownHeight, options.length, value.length]);

  useEffect(() => {
    if (isInline) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isInline]);

  const handleToggle = () => {
    if (isInline || disabled) return;
    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue: string) => {
    const option = options.find((o) => o.value === optionValue);
    if (option?.disabled) return;
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  return (
    <div
      className={`multi-select-container${isInline ? ' multi-select-container--inline-list' : ''}${
        !showInput ? ' multi-select-container--no-input' : ''
      }${
        className ? ` ${className}` : ''
      }`}
      ref={containerRef}
    >
      {showInput ? (
        <div
          ref={inputRef}
          className={`multi-select-input ${isOpen && !isInline ? 'open' : ''} ${isInline ? 'multi-select-input--inline' : ''} ${
            disabled ? 'disabled' : ''
          }`}
          onClick={isInline ? undefined : handleToggle}
          role={isInline ? 'group' : undefined}
          aria-label={isInline ? 'Selected documents' : undefined}
        >
          {selectedOptions.length === 0 ? (
            <span className="multi-select-placeholder">{placeholder}</span>
          ) : (
            <div className="multi-select-chips">
              {selectedOptions.map((option) => (
                <span key={option.value} className="multi-select-chip">
                  <span className="multi-select-chip-label">{option.label}</span>
                  <button
                    type="button"
                    className="multi-select-chip-remove"
                    onClick={(e) => handleRemove(option.value, e)}
                    disabled={disabled}
                    aria-label={`Remove ${option.label}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </span>
              ))}
            </div>
          )}
          {!isInline ? (
            <span className="multi-select-arrow" aria-hidden="true">
              <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
            </span>
          ) : null}
        </div>
      ) : null}
      {showDropdown
        ? (() => {
            const dropdown = (
              <div
                ref={dropdownRef}
                className={`multi-select-dropdown${isInline ? ' multi-select-dropdown--inline' : ''}${
                  portal ? ' multi-select-dropdown--portaled' : ''
                }${disabled ? ' multi-select-dropdown--disabled' : ''}${
                  dropdownClassName ? ` ${dropdownClassName}` : ''
                }`}
                style={
                  portal && dropdownLayout
                    ? {
                        position: 'fixed',
                        top: dropdownLayout.top,
                        left: dropdownLayout.left,
                        width: dropdownLayout.width,
                        right: 'auto',
                        maxHeight: dropdownLayout.maxHeight,
                        zIndex: 1300,
                      }
                    : undefined
                }
                role="listbox"
                aria-multiselectable="true"
                aria-label="Options"
              >
                {options.length === 0 ? (
                  <div className="multi-select-empty">No options available</div>
                ) : (
                  options.map((option) => {
                    const isSelected = value.includes(option.value);
                    const isOptionDisabled = disabled || option.disabled === true;
                    return (
                      <div
                        key={option.value}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={isOptionDisabled}
                        className={`multi-select-option ${isSelected ? 'selected' : ''} ${
                          isOptionDisabled ? 'disabled' : ''
                        }`}
                        onClick={() => {
                          if (isOptionDisabled) return;
                          handleSelect(option.value);
                        }}
                      >
                        <span className="multi-select-option-checkbox">
                          {isSelected && <i className="fas fa-check"></i>}
                        </span>
                        <span className="multi-select-option-label">{option.label}</span>
                      </div>
                    );
                  })
                )}
              </div>
            );

            if (portal && typeof document !== 'undefined') {
              return createPortal(dropdown, document.body);
            }
            return dropdown;
          })()
        : null}
    </div>
  );
};

export default MultiSelect;
